import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import localStorageNames from '@data/localStorageNames';

/**
 * Zustand store holding the CRM member identifier captured from the entry URL
 * when a customer arrives via the CRM mobile app's "Scan Table" flow.
 *
 * Flow context:
 *   CRM app scans the table QR -> appends ?crm_member_uid=<id_customer> ->
 *   shortener landing forwards the param -> useStoreFirstCredential parses it ->
 *   this store persists it -> CartContext attaches it to the transaction ->
 *   FE-Lambda stores it in Mongo -> BE-Server forwards it to CRM on bill close.
 *
 * The value is OPAQUE to the frontend (it is an id_customer per the agreed
 * contract, but CRM resolves it server-side). We only carry it, never decode it.
 *
 * It lives in its own persisted store (mirroring useStoreSiccMember) rather than
 * relying solely on the credential object, because credentials expire after
 * ~30 minutes while a member must stay attributable for the whole ordering
 * session up to checkout.
 */
export const useStoreCrmMember = create(
  persist(
    (set) => ({
      /** The captured CRM member_uid (id_customer), or null when not a CRM scan. */
      crmMember: null,
      /**
       * Persist the CRM member_uid captured at app entry.
       * @param {string|null} newMember - the crm_member_uid value, or null/empty.
       */
      updateCrmMember: (newMember) => {
        set({ crmMember: newMember });
      },
      /** Reset the CRM member (e.g. on logout / app reset). */
      clearCrmMember: () => set({ crmMember: null }),
    }),
    {
      name: localStorageNames.CRM_MEMBER,
    },
  ),
);
